import ShareArrow from '@/assets/images/icons/share-arrow.svg?react';
import DateDisplay from '@/components/common/date-display';
import type { ImageZoomActions } from '@/components/common/media-content/image-zoom-pan';
import MediaContent from '@/components/common/media-content/media-content';
import Modal from '@/components/common/modal/modal';
import { NativeShareButton } from '@/components/common/native-share-button';
import 'react-splide-ts/css';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import DelayedLayout from '@/components/layouts/progress-layout/delayed-layout';
import { MediaStateProvider, useMediaState } from '@/components/media-context';
import useDeleteCollection from '@/hooks/collections/use-delete-collection';
import useFilteredCollections from '@/hooks/collections/use-filtered-collections';
import useSendCollection from '@/hooks/collections/use-send-collection';
import useDownloadUrl from '@/hooks/use-download-url';
import useUserPlatform from '@/hooks/use-user-platform';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import { addToast } from '@/stores/toast';
import { BUILD_EMBED_TYPE } from '@/utils/constants';
import { getCollectionDownloadParams } from '@/utils/get-message-downloadable';
import { getSafeMediaUrl } from '@/utils/media-url';
import cn from '@/utils/merge-class-names';
import '@cyntler/react-doc-viewer/dist/index.css';
import { ImportCurve, Send2, Trash } from 'iconsax-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Outlet, useMatch, useNavigate } from 'react-router';
import { useTypedParams, useTypedState } from 'react-router-typesafe-routes';
import type { SplideEventHandlers } from 'react-splide-ts';
import { Splide, SplideSlide } from 'react-splide-ts';

interface Props {
  type?: ConversationType;
  id?: number;
}

export default function CollectionByIdRoute(props: Props) {
  return (
    <MediaStateProvider>
      <CollectionById {...props} />
    </MediaStateProvider>
  );
}

function CollectionById({ type: sessionType, id: sessionId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: deleteCollection } = useDeleteCollection();
  const { handleDownloadUrl, isLoading: isDownloading } = useDownloadUrl();
  const { isDesktop } = useUserPlatform();
  const [collectionId, setCollectionId] = useState(useTypedParams(ROUTES.MY.COLLECTIONS.BY_ID).collectionId);
  const { collection, collectionGroup, collectionIndexInGroup, canDownload, shouldDarkMode } = useFilteredCollections(
    collectionId,
    'forward',
    'all',
  );
  const { sendCollection } = useSendCollection(sessionType, sessionId);
  const { updatePlaybackTime, clearAllMediaState, playbackTimes } = useMediaState();
  const splideRef = useRef<Splide>(null);
  const imageZoomRef = useRef<ImageZoomActions>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleDownload = () => {
    if (!collection) return;

    const params = getCollectionDownloadParams(collection);
    if (!params) return;

    handleDownloadUrl(params.url, params.type, params.key).catch(console.warn);

    addToast({
      type: 'success',
      message: t('common.download-success'),
    });
  };

  const handleForward = async () => {
    if (!(sessionType && sessionId) || !collection) return;
    await sendCollection(collection);
    navigate(-1);
  };

  const handleDeleteCollection = async () => {
    if (
      collection &&
      (await makePrompt({
        key: 'delete-collection',
        type: 'dialog',
        title: t('my-collection.delete'),
        content: t('my-collection.confirm-delete'),
        promise: () => deleteCollection(collection.ID),
      }))
    ) {
      navigate(-1);
    }
  };

  const handleActiveSlideChange: SplideEventHandlers['onActive'] = (slide) => {
    const activeSlideCollection = collectionGroup.at(slide.index);
    if (!activeSlideCollection) return;

    setCollectionId(activeSlideCollection.ID);

    const href =
      sessionType && sessionId
        ? (sessionType === 'session'
            ? ROUTES.MESSAGES.SESSIONS.COLLECTIONS.BY_ID
            : ROUTES.MESSAGES.GROUPS.COLLECTIONS.BY_ID
          ).$buildPath({
            params: {
              id: sessionId,
              collectionId: activeSlideCollection.ID,
            },
          })
        : ROUTES.MY.COLLECTIONS.BY_ID.$buildPath({
            params: {
              collectionId: activeSlideCollection.ID,
            },
          });
    if (BUILD_EMBED_TYPE !== 'embed-native') {
      history.replaceState({ ...history.state }, '', href);
    } else {
      navigate(href, { replace: true });
    }
  };

  const enableSwipe = (enable: boolean) => {
    const splide = splideRef.current?.splide;

    if (splide) {
      splide.options = {
        ...splide.options,
        drag: enable,
      };
    }
  };

  if (!collection) {
    return <Navigate to='-1' />;
  }

  return (
    <PageLayout
      headerConfig={{
        navigateBack: true,
        className: cn({ 'bg-neutral-950 layout-mini:bg-neutral-950 text-neutral-0': shouldDarkMode }),
        headerLabel: (
          <div className='flex flex-col text-sm size-full flex-center'>
            <span className='line-clamp-1 font-bold'>{collection.FromName}</span>
            <DateDisplay
              ruleName='collection-preview'
              timestamp={collection.TimeStamp}
            />
          </div>
        ),
        actions: (
          <NativeShareButton
            className={cn(' text-white', {
              'text-base-content': !shouldDarkMode,
            })}
            content={{ type: 'collection', data: collection }}
          />
        ),
      }}
      title={t('my-collection.title')}
      bannerColor='dark'
      className={cn('flex flex-col transition-colors gap-1', {
        'bg-neutral-950 text-neutral-0': shouldDarkMode,
      })}>
      {shouldDarkMode ? (
        <DelayedLayout
          delay={300}
          className={cn({ 'flex-1 w-screen': !isDesktop })}>
          {() => (
            <Splide
              onDestroy={() => {
                clearAllMediaState();
              }}
              onDragging={() => {
                if (videoRef.current) {
                  videoRef.current?.pause();
                }
              }}
              onDragged={() => {
                if (videoRef.current) {
                  updatePlaybackTime(videoRef.current.src, videoRef.current.currentTime);
                }
              }}
              ref={splideRef}
              options={{
                perPage: 1,
                perMove: 1,
                flickMaxPages: 1,
                flickPower: 100,
                flickVelocity: 0.5,
                drag: true,
                arrows: false,
                pagination: false,
                start: collectionIndexInGroup,
                gap: '0.25rem',
                fixedWidth: '100vw',
              }}
              className={cn('flex-1', { 'carousel w-screen flex': !isDesktop })}
              onActive={handleActiveSlideChange}>
              {collectionGroup.map((m) => (
                <SplideSlide
                  key={`collection-${m.ID}`}
                  className='desktop:h-[calc(100vh-8rem)]'>
                  <MediaContent
                    ref={
                      m.ID === collectionId && m.Type === ConversationMessageContentType.Image
                        ? imageZoomRef
                        : m.Type === ConversationMessageContentType.Video
                          ? videoRef
                          : null
                    }
                    autoPlay
                    isInView={m.ID === collectionId}
                    type={m.Type as ConversationMessageContentType}
                    src={
                      m.Type === ConversationMessageContentType.File
                        ? `${m.SmallPicturePath}|${m.Content}`
                        : (m.Content ?? '')
                    }
                    playBackTime={
                      m.Type === ConversationMessageContentType.Video
                        ? playbackTimes?.[getSafeMediaUrl(m.Content)]
                        : undefined
                    }
                    onImageZoom={enableSwipe}
                  />
                </SplideSlide>
              ))}
            </Splide>
          )}
        </DelayedLayout>
      ) : (
        <MediaContent
          autoPlay
          isInView
          type={collection.Type}
          className={cn(collection.Type === ConversationMessageContentType.Text && 'px-5')}
          src={
            collection.Type === ConversationMessageContentType.File
              ? `${collection.SmallPicturePath}|${collection.Content}`
              : (collection?.Content ?? '')
          }
        />
      )}
      <div
        className={cn(
          'flex items-center justify-around desktop:justify-end desktop:py-1 w-full z-30 bg-neutral-950 pb-safe-offset-2',
          {
            'bg-neutral-0': !shouldDarkMode,
          },
        )}>
        {sessionType ? (
          <button
            type='button'
            className='btn btn-ghost flex-nowrap min-h-none desktop:gap-2 desktop:h-10'
            onClick={handleForward}>
            <Send2
              variant='Outline'
              size={24}
            />

            <span className='hidden desktop:inline'>{t('conversation.forward.send')}</span>
          </button>
        ) : (
          <>
            <button
              type='button'
              className='btn btn-ghost flex-nowrap min-h-none desktop:gap-2 desktop:h-10'
              onClick={handleDeleteCollection}>
              <Trash
                variant='Outline'
                size={24}
              />

              <span className='hidden desktop:inline'>{t('conversation.delete.delete')}</span>
            </button>

            {canDownload && (
              <ProgressButton
                as='button'
                className='btn btn-ghost flex-nowrap min-h-none desktop:gap-2 desktop:h-10'
                isLoading={isDownloading}
                label={isDesktop ? 'conversation.setting.group.qr-code.save-image' : ''}
                onClick={handleDownload}
                icon={
                  <ImportCurve
                    variant='Outline'
                    size={24}
                  />
                }
              />
            )}

            <Link
              to={ROUTES.MY.COLLECTIONS.BY_ID.$.FORWARD.$path({ relative: true })}
              className='btn btn-ghost flex-nowrap min-h-none desktop:gap-2 desktop:h-10'>
              <ShareArrow className='size-6' />

              <span className='hidden desktop:inline'>{t('conversation.forward.forward')}</span>
            </Link>
          </>
        )}
      </div>
    </PageLayout>
  );
}

export function CollectionByIdLayout() {
  const isForwarding = useMatch({ path: ROUTES.MY.COLLECTIONS.BY_ID.FORWARD.$path(), end: true });
  const navigate = useNavigate();
  const { isDesktop } = useUserPlatform();
  const { skipPreview } = useTypedState(ROUTES.MY.COLLECTIONS.BY_ID.FORWARD);

  if (!isDesktop) {
    if (isForwarding) {
      return <Outlet />;
    }
    return <CollectionByIdRoute />;
  }

  return (
    <>
      {!skipPreview && <CollectionByIdRoute />}
      {isForwarding && (
        <Modal
          contentClassName='flex bg-red-500 p-0 max-w-[400px] w-[400px] h-92dvh max-h-[52rem]'
          defaultOpen
          onClose={() => {
            navigate(-1);
          }}>
          <div className='flex flex-1 gap-y-2'>
            <Outlet />
          </div>
        </Modal>
      )}
    </>
  );
}
